import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputBase,
  Checkbox,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete
} from '@mui/material';
import { useNotification } from '../layouts/Layout';
import { Search, MoreVert, Refresh, Delete, Visibility } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import serviceService, { Service } from '../../services/serviceService'; // Importujemy serviceService i interfejs Service
import AddOrderForm from './AddOrderForm'; // Może być potrzebny inny formularz dla usług
import projectService from '../../services/projectService';

interface ServicesTabProps {
  projectName: string;
  projectId: string;
}

// Lista miesięcy
const months = [
  'Wszystkie',
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

// Mapowanie nazw miesięcy na numery
const monthToNumber: Record<string, number> = {
  'Wszystkie': 0,
  'Styczeń': 1, 'Luty': 2, 'Marzec': 3, 'Kwiecień': 4, 'Maj': 5, 'Czerwiec': 6,
  'Lipiec': 7, 'Sierpień': 8, 'Wrzesień': 9, 'Październik': 10, 'Listopad': 11, 'Grudzień': 12
};

// Funkcja pomocnicza do sprawdzania, czy tekst jest poprawnym URL-em
const isValidUrl = (text: string): boolean => {
  try {
    new URL(text);
    return true;
  } catch (e) {
    return false;
  }
};

const ServicesTab: React.FC<ServicesTabProps> = ({ projectName, projectId }) => {
  const [services, setServices] = useState<Service[]>([]); // Zmieniono na usługi
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('Wszystkie');
  const [selectedYearMonth, setSelectedYearMonth] = useState('miesiąc');
  const [filteredServices, setFilteredServices] = useState<Service[]>([]); // Zmieniono na usługi
  const [yearMonthOptions, setYearMonthOptions] = useState<string[]>(['miesiąc']);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentService, setCurrentService] = useState<Service | null>(null); // Zmieniono na usługę
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('Wszyscy'); // Stan dla wybranego kontrahenta
  
  // Stan dla okna dialogowego zamówienia (może być potrzebny dla usług)
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const [orderContent, setOrderContent] = useState('');
  const [orderContractor, setOrderContractor] = useState('');

  // Obliczanie sumy wartości
  const totalValue = filteredServices.reduce((sum, item) => sum + item.value, 0);

  // Pobieranie usług z API
  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await serviceService.getServicesByProject(projectId);
      setServices(data);
      setFilteredServices(data);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas pobierania usług:', error);
      setError('Nie udało się pobrać usług. Spróbuj ponownie później.');
      showNotification('Nie udało się pobrać usług. Spróbuj ponownie później.', 'error');
      setLoading(false);
    }
  };

  // Importowanie usług z MSSQL
  const importServicesFromMssql = async () => {
    try {
      setImportLoading(true);
      setError(null);
      const result = await serviceService.importServicesFromMssql(projectId);
      showNotification(`Pomyślnie zaimportowano ${result.services.length} usług`, 'success');
      fetchServices(); // Odświeżenie listy usług po imporcie
    } catch (error) {
      console.error('Błąd podczas importowania usług z MSSQL:', error);
      setError('Nie udało się zaimportować usług. Spróbuj ponownie później.');
      showNotification('Nie udało się zaimportować usług. Spróbuj ponownie później.', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Usuwanie usługi
  const handleDeleteService = async () => {
    if (currentService?.id) {
      try {
        setLoading(true);
        setError(null);
        await serviceService.deleteService(currentService.id);
        showNotification('Usługa została usunięta', 'success');
        fetchServices(); // Odświeżenie listy usług po usunięciu
        handleCloseMenu();
      } catch (error) {
        console.error('Błąd podczas usuwania usługi:', error);
        setError('Nie udało się usunąć usługi. Spróbuj ponownie później.');
        showNotification('Nie udało się usunąć usługi. Spróbuj ponownie później.', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Obsługa menu kontekstowego
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, service: Service) => {
    setAnchorEl(event.currentTarget);
    setCurrentService(service);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setCurrentService(null);
  };

  // Obsługa otwierania/zamykania okna dialogowego szczegółów
  const handleOpenDetails = () => {
    setDetailsOpen(true);
    setAnchorEl(null); // Zamknij menu bez resetowania currentService
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };


  // Pobieranie usług przy pierwszym renderowaniu
  useEffect(() => {
    if (projectId) {
      fetchServices();
    }
  }, [projectId]);

  // Generowanie opcji roku i miesiąca, kontrahentów i miejscowości na podstawie dostępnych danych
  useEffect(() => {
    const yearMonthOptionsSet = new Set<string>(['miesiąc']);
    const clientOptionsSet = new Set<string>(['Wszyscy']);
    const locationOptionsSet = new Set<string>(['Wszystkie']);
    
    services.forEach(item => {
      // Opcje roku i miesiąca
      const dateParts = item.date.split('-');
      if (dateParts.length === 3) {
        const year = dateParts[0];
        const month = parseInt(dateParts[1]);
        
        const monthName = Object.keys(monthToNumber).find(
          key => monthToNumber[key] === month
        ) || '';
        
        if (monthName && monthName !== 'Wszystkie') {
          yearMonthOptionsSet.add(`${monthName} ${year}`);
        }
      }
      // Opcje kontrahentów
      if (item.client) {
        clientOptionsSet.add(item.client);
      }
      // Opcje miejscowości
      if (item.location) {
        locationOptionsSet.add(item.location);
      }
    });
    
    setYearMonthOptions(Array.from(yearMonthOptionsSet));
    // Ustawienie opcji kontrahentów, jeśli są jacyś kontrahenci
    if (clientOptionsSet.size > 1) {
      setClientOptions(Array.from(clientOptionsSet));
    } else {
      setClientOptions(['Wszyscy']); // Domyślna opcja, jeśli brak kontrahentów
    }
    // Ustawienie opcji miejscowości, jeśli są jakieś miejscowości
    if (locationOptionsSet.size > 1) {
      setLocationOptions(Array.from(locationOptionsSet));
    } else {
      setLocationOptions(['Wszystkie']); // Domyślna opcja, jeśli brak miejscowości
    }
  }, [services]);

  // Stan dla opcji filtrowania
  const [clientOptions, setClientOptions] = useState<string[]>(['Wszyscy']);
  const [locationOptions, setLocationOptions] = useState<string[]>(['Wszystkie']);

  // Obsługa zmiany wyszukiwania
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    applyFilters(event.target.value, selectedLocation, selectedYearMonth, selectedClient);
  };

  // Obsługa zmiany lokalizacji
  const handleLocationChange = (event: SelectChangeEvent) => {
    setSelectedLocation(event.target.value);
    applyFilters(searchTerm, event.target.value, selectedYearMonth, selectedClient);
  };

  // Obsługa zmiany roku i miesiąca
  const handleYearMonthChange = (event: SelectChangeEvent) => {
    setSelectedYearMonth(event.target.value);
    applyFilters(searchTerm, selectedLocation, event.target.value, selectedClient);
  };

  // Obsługa zmiany kontrahenta
  const handleClientChange = (event: SelectChangeEvent) => {
    setSelectedClient(event.target.value);
    applyFilters(searchTerm, selectedLocation, selectedYearMonth, event.target.value);
  };

  // Obsługa zaznaczania wszystkich elementów
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      const allIds = filteredServices.map(item => item.service_id);
      setSelected(allIds);
    } else {
      setSelected([]);
    }
  };

  // Obsługa zaznaczania pojedynczego elementu
  const handleSelect = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter(itemId => itemId !== id);
    }

    setSelected(newSelected);
    setSelectAll(newSelected.length === filteredServices.length);
  };

  // Aktualizacja zaznaczenia usługi
  const handleToggleSelection = async (id: number, isSelected: boolean) => {
    try {
      await serviceService.toggleServiceSelection(id, isSelected);
      // Aktualizacja lokalnego stanu po pomyślnej aktualizacji w bazie danych
      const updatedServices = services.map(service => 
        service.id === id ? { ...service, is_selected: isSelected } : service
      );
      setServices(updatedServices);
      
      // Aktualizacja filtrowanych usług
      const updatedFilteredServices = filteredServices.map(service => 
        service.id === id ? { ...service, is_selected: isSelected } : service
      );
      setFilteredServices(updatedFilteredServices);
    } catch (error) {
      console.error('Błąd podczas aktualizacji zaznaczenia usługi:', error);
      setError('Nie udało się zaktualizować zaznaczenia usługi. Spróbuj ponownie później.');
      showNotification('Nie udało się zaktualizować zaznaczenia usługi. Spróbuj ponownie później.', 'error');
    }
  };

  // Funkcja filtrowania
  const applyFilters = (search: string, location: string, yearMonth: string, client: string) => {
    let filtered = services;
    
    // Filtrowanie po symbolu
    if (search) {
      filtered = filtered.filter(item => 
        item.symbol.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Filtrowanie po lokalizacji
    if (location !== 'Wszystkie') {
      filtered = filtered.filter(item => 
        item.location === location
      );
    }

    // Filtrowanie po kontrahencie
    if (client !== 'Wszyscy') {
      filtered = filtered.filter(item => item.client === client);
    }
    
    // Filtrowanie po roku i miesiącu
    if (yearMonth !== 'miesiąc') {
      const [monthName, year] = yearMonth.split(' ');
      const monthNumber = monthToNumber[monthName];
      
      filtered = filtered.filter(item => {
        const dateParts = item.date.split('-');
        if (dateParts.length === 3) {
          const itemYear = dateParts[0];
          const itemMonth = parseInt(dateParts[1]);
          return itemMonth === monthNumber && itemYear === year;
        }
        return false;
      });
    }
    
    setFilteredServices(filtered);
    // Aktualizacja zaznaczenia po filtrowaniu
    setSelectAll(selected.length > 0 && selected.length === filtered.length);
  };

  // Obsługa otwierania okna dialogowego zamówienia
  const handleOpenOrderForm = () => {
    // Przygotowanie treści zamówienia z zaznaczonych usług
    const selectedServices = filteredServices.filter(service => 
      selected.includes(service.service_id)
    );

    // Tworzenie listy numerowanej usług z jednostką miary w nawiasach
    const orderContentText = selectedServices.map((service, index) => 
      `${index + 1}. ${service.symbol}${service.unit ? ` (${service.unit})` : ''}`
    ).join('\n');

    setOrderContent(orderContentText);

    // Jeśli wszystkie zaznaczone usługi mają ten sam email, ustaw go jako kontrahenta
    const emails = new Set(selectedServices.map(service => service.email).filter(Boolean));
    if (emails.size === 1) {
      const email = Array.from(emails)[0];
      if (email) {
        setOrderContractor(email);
      }
    } else {
      setOrderContractor('');
    }

    setOrderFormOpen(true);
  };

  // Obsługa zamykania okna dialogowego zamówienia
  const handleCloseOrderForm = () => {
    setOrderFormOpen(false);
  };

  // Obsługa dodawania zamówienia
  const handleAddOrder = async (orderData: any) => {
    try {
      setLoading(true);
      // Używamy project_id z formularza, a nie z kontekstu
      const orderPayload = { ...orderData };
      
      await projectService.createOrder(orderPayload); // Użycie projectService.createOrder
      
      // Jeśli zamówienie zostało dodane do innego projektu niż bieżący,
      // wyświetlamy odpowiednią informację
      if (orderData.project_id && orderData.project_id !== projectId) {
        showNotification(`Zamówienie zostało dodane do projektu ${orderData.project_id}`, 'success');
      } else {
        showNotification('Zamówienie zostało dodane', 'success');
      }
      
      setSelected([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Błąd podczas dodawania zamówienia:', error);
      setError('Nie udało się dodać zamówienia. Spróbuj ponownie później.');
      showNotification('Nie udało się dodać zamówienia. Spróbuj ponownie później.', 'error');
    } finally {
      setLoading(false);
      setOrderFormOpen(false);
    }
  };

  return (
    <Box>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Usługi
      </Typography>
      
      {/* Pasek filtrowania i akcji */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {/* Pole wyszukiwania */}
          <TextField
            placeholder="Szukaj po symbolu..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{ width: '350px' }}
            InputProps={{
              style: { fontSize: '0.75rem' },
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#999', fontSize: '16px' }} />
                </InputAdornment>
              )
            }}
            InputLabelProps={{
              style: { fontSize: '0.75rem' }
            }}
          />
          
          {/* Filtr rok, miesiąc - Autocomplete */}
          <Autocomplete
            id="year-month-autocomplete"
            options={yearMonthOptions}
            value={selectedYearMonth}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedYearMonth(newValue);
                applyFilters(searchTerm, selectedLocation, newValue, selectedClient);
              }
            }}
            sx={{ width: '350px' }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Miesiąc" 
                variant="outlined" 
                size="small"
                InputProps={{
                  ...params.InputProps,
                  style: { fontSize: '0.75rem' },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: '16px' }} />
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{
                  style: { fontSize: '0.75rem' }
                }}
              />
            )}
          />
        </Box>

        {/* Filtr Kontrahent - Autocomplete */}
        <Autocomplete
          id="client-autocomplete"
          options={clientOptions}
          value={selectedClient}
          onChange={(event, newValue) => {
            if (newValue) {
              setSelectedClient(newValue);
              applyFilters(searchTerm, selectedLocation, selectedYearMonth, newValue);
            }
          }}
          sx={{ width: '350px' }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Kontrahent" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#999', fontSize: '16px' }} />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                style: { fontSize: '0.75rem' }
              }}
            />
          )}
        />
        
        {/* Filtr Miejscowość - Autocomplete */}
        <Autocomplete
          id="location-autocomplete"
          options={locationOptions}
          value={selectedLocation}
          onChange={(event, newValue) => {
            if (newValue) {
              setSelectedLocation(newValue);
              applyFilters(searchTerm, newValue, selectedYearMonth, selectedClient);
            }
          }}
          sx={{ width: '350px' }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Miejscowość" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#999', fontSize: '16px' }} />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                style: { fontSize: '0.75rem' }
              }}
            />
          )}
        />
      </Box>

      {/* Przyciski akcji */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'flex-end' }}>
        {selected.length > 0 && (
          <Tooltip title="Zamów zaznaczone usługi">
            <Button 
              variant="contained" 
              size="small" 
              color="success"
              onClick={handleOpenOrderForm}
            >
              Zamów
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Aktualizuj dane z MSSQL">
          <Button 
            variant="contained" 
            size="small" 
            onClick={importServicesFromMssql}
            disabled={importLoading || loading}
            startIcon={importLoading ? <CircularProgress size={20} /> : <Refresh />}
          >
            Aktualizuj
          </Button>
        </Tooltip>
      </Box>
      
      {/* Tabela usług */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', maxHeight: '450px', overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={selected.length > 0 && selected.length < filteredServices.length}
                />
              </TableCell>
              <TableCell width="100px" sx={{ whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Data</TableCell>
              <TableCell width="180px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Symbol</TableCell>
              <TableCell width="180px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Kontrahent</TableCell>
              <TableCell width="180px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Miejscowość</TableCell>
              <TableCell align="right" width="80px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Ilość</TableCell>
              <TableCell align="center" width="80px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>J.m.</TableCell>
              <TableCell align="right" width="100px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Cena jedn.</TableCell>
              <TableCell align="right" width="100px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Wartość</TableCell>
              <TableCell width="180px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Email</TableCell>
              <TableCell width="50px" align="center" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Akcja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& tr:last-child td, & tr:last-child th': { border: 0 } }}>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredServices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Brak usług do wyświetlenia.
                </TableCell>
              </TableRow>
            ) : (
              filteredServices.map((service) => (
                <TableRow 
                  key={service.id} 
                  hover
                  selected={selected.indexOf(service.service_id) !== -1}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.indexOf(service.service_id) !== -1}
                      onChange={() => handleSelect(service.service_id)}
                      onClick={() => service.id && handleToggleSelection(service.id, !service.is_selected)}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {service.date ? new Date(service.date).toISOString().split('T')[0] : ''}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{service.symbol}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{service.client}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{service.location}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{typeof service.quantity === 'number' ? service.quantity : parseFloat(service.quantity as any) || 0}</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{service.unit || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{typeof service.unit_price === 'number' ? service.unit_price.toFixed(2) : (parseFloat(service.unit_price as any) || 0).toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{typeof service.value === 'number' ? service.value.toFixed(2) : (parseFloat(service.value as any) || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{service.email}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(event) => handleOpenMenu(event, service)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
            <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
              <TableCell padding="checkbox" sx={{ borderTop: '1px solid #eee', fontSize: '0.75rem' }}></TableCell>
              <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                Suma:
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                {totalValue.toFixed(2)}
              </TableCell>
              <TableCell colSpan={2} sx={{ borderTop: '1px solid #eee', fontSize: '0.75rem' }}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu kontekstowe */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleOpenDetails}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          Szczegóły
        </MenuItem>
      </Menu>

      {/* Okno dialogowe szczegółów usługi */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Szczegóły usługi</DialogTitle>
        <DialogContent>
          {currentService && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>ID Usługi:</strong> {currentService.service_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>ID Projektu:</strong> {currentService.project_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Data:</strong> {currentService.date ? new Date(currentService.date).toISOString().split('T')[0] : ''}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Symbol:</strong> {currentService.symbol}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Kontrahent:</strong> {currentService.client || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Miejscowość:</strong> {currentService.location}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Ilość:</strong> {currentService.quantity}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Jednostka miary:</strong> {currentService.unit || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Cena jednostkowa:</strong> {typeof currentService.unit_price === 'number' ? currentService.unit_price.toFixed(2) : (parseFloat(currentService.unit_price as any) || 0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Wartość:</strong> {typeof currentService.value === 'number' ? currentService.value.toFixed(2) : (parseFloat(currentService.value as any) || 0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Email:</strong> {currentService.email || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Kontakt:</strong> {currentService.contact || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Uwagi:</strong>{' '}
                  {currentService.comments ? (
                    isValidUrl(currentService.comments) ? (
                      <a href={currentService.comments} target="_blank" rel="noopener noreferrer">
                        {currentService.comments}
                      </a>
                    ) : (
                      currentService.comments
                    )
                  ) : (
                    '-'
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Zaznaczony:</strong> {currentService.is_selected ? 'Tak' : 'Nie'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Data utworzenia:</strong> {currentService.created_at ? new Date(currentService.created_at).toLocaleString() : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Data aktualizacji:</strong> {currentService.updated_at ? new Date(currentService.updated_at).toLocaleString() : '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary">
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Okno dialogowe zamówienia */}
      <AddOrderForm
        open={orderFormOpen}
        onClose={handleCloseOrderForm}
        onAdd={handleAddOrder}
        onEdit={() => {}}
        onDelete={() => {}}
        editMode={false}
        orderData={{
          contractor: orderContractor,
          order_content: orderContent
        }}
        currentProjectId={projectId}
      />
    </Box>
  );
};

export default ServicesTab;
